import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchActiveEndpoints,
  getPushRegistration,
  getVapidPublicKey,
  isWebPushSupported,
  registerPushWorker,
  registerSubscriptionWithServer,
  subscribeThisBrowser,
  subscriptionUsesKey,
  unregisterCurrentBrowserPush,
} from '../lib/webPush';

/**
 * unsupported     the browser has no Push API (plain iOS Safari, old browsers)
 * not-configured  this build has no VAPID public key
 * denied          the user blocked notifications for the site
 * off / on        whether this browser gets pushes for the signed-in user
 * busy            loading, or switching on/off
 */
export type WebPushState = 'unsupported' | 'not-configured' | 'denied' | 'off' | 'on' | 'busy';

export interface UseWebPushSubscriptionOptions {
  /**
   * On mount, re-register a browser that is already switched on (bumps
   * last_seen_at and swaps in a new VAPID key after a rotation), and handle
   * the worker's pushsubscriptionchange message. Only the always-mounted bell
   * passes this, so it runs once per page load.
   */
  heartbeat?: boolean;
  /**
   * Read the user's other browsers for `otherBrowsers` (default true). The bell
   * turns it off: it only needs this browser's state, and without a local
   * subscription that needs no network call.
   */
  countOtherBrowsers?: boolean;
}

export interface WebPushSubscriptionControls {
  state: WebPushState;
  /** Other browsers switched on for this user (not counting this one). */
  otherBrowsers: number;
  error: string | null;
  /** Asks for permission (from the click that calls it) and switches this browser on. */
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  /** Switches every browser off for this user, this one included. */
  disableEverywhere: () => Promise<void>;
}

/** Fired on window after this tab switches push on or off, so other hook instances refresh. */
export const WEB_PUSH_CHANGED_EVENT = 'web-push-changed';

function announceChange() {
  window.dispatchEvent(new Event(WEB_PUSH_CHANGED_EVENT));
}

/** The states that need no network call to work out, or null when it depends on the subscription. */
function staticState(): 'unsupported' | 'not-configured' | 'denied' | null {
  if (!getVapidPublicKey()) return 'not-configured';
  if (!isWebPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return null;
}

function currentPermission(): NotificationPermission | 'unsupported' {
  return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported';
}

/**
 * Keeps an opted-in browser registered. "Opted in" means the server has an
 * active row for this browser's endpoint (or, after pushsubscriptionchange,
 * for the endpoint it replaced). A local subscription the signed-in user has no
 * active row for is unsubscribed instead: it was switched off with "Turn off
 * everywhere", or it belongs to someone who used this browser before.
 * Returns whether it changed anything.
 */
async function syncThisBrowser(previousEndpoint: string | null = null): Promise<boolean> {
  if (staticState() !== null || Notification.permission !== 'granted') return false;

  const registration = await getPushRegistration();
  if (!registration) return false;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription && !previousEndpoint) return false;

  const activeEndpoints = await fetchActiveEndpoints();
  if (activeEndpoints === null) return false;

  const optedIn =
    (subscription !== null && activeEndpoints.includes(subscription.endpoint)) ||
    (previousEndpoint !== null && activeEndpoints.includes(previousEndpoint));
  if (!optedIn) {
    if (!subscription) return false;
    await subscription.unsubscribe();
    return true;
  }

  const staleEndpoints = previousEndpoint ? [previousEndpoint] : [];
  if (subscription && !subscriptionUsesKey(subscription, getVapidPublicKey())) {
    staleEndpoints.push(subscription.endpoint);
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await subscribeThisBrowser(registration);

  const { error } = await registerSubscriptionWithServer(subscription);
  if (error) throw error;

  const endpointsToUnregister = staleEndpoints.filter((endpoint) => endpoint !== subscription.endpoint);
  if (endpointsToUnregister.length > 0) {
    await supabase.rpc('unregister_web_push_subscriptions', { p_endpoints: endpointsToUnregister });
  }
  return true;
}

/**
 * Browser push for the signed-in user on this browser: the Profile Settings
 * switch and the bell's one-line hint. The permission prompt is only ever
 * raised by enable(), which must be called from a click handler.
 */
export function useWebPushSubscription(options: UseWebPushSubscriptionOptions = {}): WebPushSubscriptionControls {
  const { heartbeat = false, countOtherBrowsers = true } = options;
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [status, setStatus] = useState<{ state: WebPushState; otherBrowsers: number }>(() => ({
    state: staticState() ?? 'busy',
    otherBrowsers: 0,
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const permissionRef = useRef(currentPermission());

  const refresh = useCallback(async () => {
    permissionRef.current = currentPermission();
    const fixed = staticState();
    if (fixed !== null || !userId) {
      if (mountedRef.current) setStatus({ state: fixed ?? 'off', otherBrowsers: 0 });
      return;
    }

    try {
      const registration = await getPushRegistration();
      const subscription = (await registration?.pushManager.getSubscription()) ?? null;
      const thisEndpoint = subscription?.endpoint ?? null;
      const activeEndpoints = thisEndpoint !== null || countOtherBrowsers ? await fetchActiveEndpoints() : [];
      const on =
        thisEndpoint !== null &&
        Notification.permission === 'granted' &&
        (activeEndpoints === null || activeEndpoints.includes(thisEndpoint));
      const otherBrowsers = activeEndpoints ? activeEndpoints.filter((endpoint) => endpoint !== thisEndpoint).length : 0;
      if (mountedRef.current) setStatus({ state: on ? 'on' : 'off', otherBrowsers });
    } catch (err) {
      console.warn('Could not read this browser\'s push subscription:', err);
      if (mountedRef.current) setStatus({ state: 'off', otherBrowsers: 0 });
    }
  }, [userId, countOtherBrowsers]);

  useEffect(() => {
    mountedRef.current = true;
    const run = async () => {
      if (heartbeat && userId) {
        try {
          if (await syncThisBrowser()) {
            // Refreshes this instance too, and the Settings section if it is open.
            announceChange();
            return;
          }
        } catch (err) {
          console.warn('Web push heartbeat failed:', err);
        }
      }
      await refresh();
    };
    void run();

    const handleChanged = () => {
      void refresh();
    };
    // Re-read the permission when the user comes back, e.g. after unblocking
    // notifications in the browser's site settings.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && currentPermission() !== permissionRef.current) {
        void refresh();
      }
    };
    window.addEventListener(WEB_PUSH_CHANGED_EVENT, handleChanged);
    document.addEventListener('visibilitychange', handleVisibility);

    const container = heartbeat && userId && isWebPushSupported() ? navigator.serviceWorker : null;
    const handleWorkerMessage = (event: MessageEvent) => {
      const data: unknown = event.data;
      if (!data || typeof data !== 'object') return;
      const message = data as { type?: unknown; oldEndpoint?: unknown };
      if (message.type !== 'pushsubscriptionchange') return;
      const oldEndpoint = typeof message.oldEndpoint === 'string' ? message.oldEndpoint : null;
      void syncThisBrowser(oldEndpoint)
        .catch((err: unknown) => console.warn('Web push re-subscribe failed:', err))
        .finally(announceChange);
    };
    container?.addEventListener('message', handleWorkerMessage);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(WEB_PUSH_CHANGED_EVENT, handleChanged);
      document.removeEventListener('visibilitychange', handleVisibility);
      container?.removeEventListener('message', handleWorkerMessage);
    };
  }, [heartbeat, userId, refresh]);

  const enable = useCallback(async () => {
    if (!userId || !isWebPushSupported() || !getVapidPublicKey()) return;
    setBusy(true);
    setError(null);
    try {
      // First await in the click handler: browsers only show the prompt while
      // the click's user activation is still fresh.
      const permission =
        Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
      if (permission !== 'granted') return;

      const registration = await registerPushWorker();
      let subscription = await registration.pushManager.getSubscription();
      if (subscription && !subscriptionUsesKey(subscription, getVapidPublicKey())) {
        await subscription.unsubscribe();
        subscription = null;
      }
      subscription ??= await subscribeThisBrowser(registration);

      const { error: registerError } = await registerSubscriptionWithServer(subscription);
      if (registerError) {
        await subscription.unsubscribe().catch(() => false);
        throw registerError;
      }
      announceChange();
    } catch (err) {
      console.warn('Could not switch browser notifications on:', err);
      if (mountedRef.current) setError('Couldn\'t turn on notifications for this browser. Please try again.');
    } finally {
      if (mountedRef.current) setBusy(false);
      await refresh();
    }
  }, [userId, refresh]);

  const disable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await unregisterCurrentBrowserPush();
      announceChange();
    } finally {
      if (mountedRef.current) setBusy(false);
      await refresh();
    }
  }, [refresh]);

  const disableEverywhere = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await unregisterCurrentBrowserPush();
      const { data: { session } } = await supabase.auth.getSession();
      const authUserId = session?.user.id;
      if (!authUserId) throw new Error('Not signed in');
      // Owner DELETE via RLS. Each other browser drops its local subscription
      // on its next visit, because its endpoint no longer has a row.
      const { error: deleteError } = await supabase
        .from('web_push_subscriptions')
        .delete()
        .eq('user_id', authUserId);
      if (deleteError) throw new Error(deleteError.message);
      announceChange();
    } catch (err) {
      console.warn('Could not switch browser notifications off everywhere:', err);
      if (mountedRef.current) setError('Couldn\'t turn notifications off in your other browsers. Please try again.');
    } finally {
      if (mountedRef.current) setBusy(false);
      await refresh();
    }
  }, [refresh]);

  return {
    state: busy ? 'busy' : status.state,
    otherBrowsers: status.otherBrowsers,
    error,
    enable,
    disable,
    disableEverywhere,
  };
}
